'use client'

import Cards from '@/ui/assets/components/cards.inline.svg'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { HelpCircle, ShieldX } from 'lucide-react'

interface CardKYCRejectedProps {
  onContactSupport: () => void
}

export function CardKYCRejected({ onContactSupport }: CardKYCRejectedProps) {
  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-red-500/10 via-transparent to-red-400/5" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-red-500/30 bg-red-500/10">
                <ShieldX className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <Typography variant="h3" className="text-red-400">
                  KYC Application Rejected
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              Unfortunately, your KYC application has been rejected. This could be due to incomplete documentation,
              information mismatches, or regulatory compliance requirements.
            </Typography>

            <div className="flex flex-col gap-3 rounded-lg border border-border/50 bg-background/30 p-4">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Incomplete or unclear documentation
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Information mismatch between documents
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Documents not meeting requirements
                </Typography>
              </div>
            </div>

            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
              <Typography variant="p" className="text-red-400 text-sm">
                <strong>What you can do:</strong> Contact our support team for detailed feedback on your application.
              </Typography>
            </div>

            <Button
              onClick={onContactSupport}
              size="lg"
              className="w-full transition-all duration-300 hover:opacity-90 hover:shadow-lg"
            >
              Contact Support
            </Button>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-red-500/10 to-red-400/10 blur-[60px]" />
        </div>
        <div className="relative opacity-60 grayscale transition-all duration-500 hover:opacity-80 hover:grayscale-0">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}
